<template>
  <div
    v-if="heading?.[locale] || images.length > 0"
    :class="[
      'hero-stack bp1680:w-[863px] mx-auto flex w-[277px] flex-col sm:w-[365px] md:w-[604px] xl:w-[740px] 2xl:w-[863px]',
      className || 'mb-[var(--gap)]',
    ]"
  >
    <h2
      v-if="heading?.[locale]"
      class="mb-5 text-1.5xl/[115%] xl:mb-10 xl:text-[26px]/[115%]"
    >
      {{ heading?.[locale] }}
    </h2>

    <button
      v-if="images && images.length > 0"
      @click="openModal"
      class="group relative flex w-full cursor-pointer flex-col items-center"
      :style="{
        marginBottom: `calc(var(--stack-overflow) * ${renderedImagesCount - 1})`,
      }"
      :aria-label="t('common.open.image.modal')"
    >
      <NuxtImg
        v-if="images.length === 1"
        :src="images?.[0]?.url"
        :alt="images?.[0]?.alt_text ?? 'Hero Image'"
        format="webp"
        sizes="xs:280px sm:365px md:604px xl:740px xxl:865px"
        :preload="{ fetchPriority: 'high' }"
        class="object-cover shadow-[0_4.134px_4.134px_rgba(0,0,0,0.25)] sm:shadow-[0_5px_5px_rgba(0,0,0,0.25)] md:shadow-[0_4.082px_4.082px_rgba(0,0,0,0.25)] min-[1440px]:shadow-[0_5px_5px_rgba(0,0,0,0.25)] min-[1680px]:shadow-[0_5.833px_5.833px_rgba(0,0,0,0.25)]"
        :style="{ width: '100%', height: 'auto' }"
      />

      <NuxtImg
        v-if="images.length > 1"
        v-for="(image, index) in images?.slice(0, 3)"
        :key="image.id"
        :src="image.url"
        :alt="image.alt_text ?? 'Hero Image'"
        format="webp"
        sizes="xs:280px sm:365px md:604px xl:740px xxl:865px"
        :preload="index === 0 ? { fetchPriority: 'high' } : undefined"
        :class="[
          'object-cover shadow-[0_4.134px_4.134px_rgba(0,0,0,0.25)] transition-transform duration-500 ease-out will-change-transform sm:shadow-[0_5px_5px_rgba(0,0,0,0.25)] md:shadow-[0_4.082px_4.082px_rgba(0,0,0,0.25)] min-[1440px]:shadow-[0_5px_5px_rgba(0,0,0,0.25)] min-[1680px]:shadow-[0_5.833px_5.833px_rgba(0,0,0,0.25)]',
          index === 0
            ? 'relative group-hover:-translate-y-2 md:group-hover:-translate-y-3 xl:group-hover:-translate-y-4'
            : 'absolute left-1/2 -translate-x-1/2',

          index === 1
            ? 'group-hover:translate-y-2.5 md:group-hover:translate-y-4 xl:group-hover:translate-y-6'
            : '',
          index === 2
            ? 'group-hover:translate-y-4 md:group-hover:translate-y-7 xl:group-hover:translate-y-10'
            : '',
        ]"
        :style="{
          width: `calc(100% - ${index} * var(--width-offset) * 2)`,
          aspectRatio: '5/3.33',
          zIndex: 30 - index,
          top: index === 0 ? 'auto' : `calc(${index} * var(--top-offset))`,
        }"
      />
    </button>
  </div>

  <ImageModal
    v-if="images && images.length > 0"
    :is-open="isModalOpen"
    :images="images"
    @close="closeModal"
  />
</template>

<script setup lang="ts">
import type { ImageModalType, LocalizedString } from '~/types'

defineOptions({ inheritAttrs: false })

interface Props {
  images: ImageModalType[]
  className?: string
  background?: string
  heading?: LocalizedString
}

const props = defineProps<Props>()
const { t, locale } = useI18n()
const isModalOpen = ref(false)

const openModal = () => {
  isModalOpen.value = true
}

const closeModal = () => {
  isModalOpen.value = false
}

const renderedImagesCount = computed(() => {
  const list = props.images && props.images.length > 0 ? props.images : []
  return Math.min(list.length, 3)
})
</script>

<style scoped>
.hero-stack {
  --gap: 40px;
  --top-offset: 47.92px;
  --width-offset: 13.84px;
}

@media (min-width: 768px) {
  .hero-stack {
    --gap: 60px;
    --top-offset: 63.15px;
    --width-offset: 18.25px;
  }
}

@media (min-width: 1440px) {
  .hero-stack {
    --gap: 80px;
    --top-offset: 104.51px;
    --width-offset: 30.2px;
  }
}

@media (min-width: 1680px) {
  .hero-stack {
    --gap: 80px;
    --top-offset: 128px;
    --width-offset: 37px;
  }
}

.hero-stack button {
  --stack-overflow: calc(var(--top-offset) - var(--width-offset) * 1.332);
}
</style>
